import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {useGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
import type {
  CopilotChatModel,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotChatThread,
  CopilotSpaceId,
  MediaContentItem,
} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {useSpaceSelection} from '@github-ui/copilot-spaces/hooks/use-space-selection'
import {useCallback} from 'react'

import {makeVersionedItemFromReference} from '../components/ContentPreview/content-preview-types'
import {useContentPreview} from '../components/ContentPreview/ContentPreviewContext'
import {useIsSharedThread} from './use-is-shared-thread'
import {useRouteThreadId} from './use-route-thread-id'

export type HandleMessageSubmit = (
  content: string,
  additionalReferences?: CopilotChatReference[],
  model?: CopilotChatModel,
  initialCustomCopilotId?: CopilotSpaceId | null,
  mediaContent?: MediaContentItem[],
  thread?: CopilotChatThread | null,
  onAccepted?: () => void,
) => Promise<boolean | void>

/**
 * Custom hook that provides the handleUserSubmit function for chat interactions
 * @param currentTopicRef - React ref containing the current topic
 * @param nextMessageIndex - The index of the next message in the conversation
 * @returns handleUserSubmit function that can be used to send user messages
 */
export function useHandleMessageSubmit(
  currentTopicRef: React.RefObject<CopilotChatRepo | undefined>,
  nextMessageIndex: number,
): HandleMessageSubmit {
  const getChatState = useGetChatState()
  const manager = useChatManager()
  const {reloadQuota} = useEntitlement()
  const {updateItem} = useContentPreview()
  const isSharedThread = useIsSharedThread()
  const threadIdFromRoute = useRouteThreadId()

  const {activeCopilotSpaceId: customCopilotId} = useSpaceSelection()

  return useCallback(
    async (
      content: string,
      additionalReferences?: CopilotChatReference[],
      model?: CopilotChatModel,
      initialCustomCopilotId?: CopilotSpaceId | null,
      mediaContent?: MediaContentItem[],
      thread?: CopilotChatThread | null,
      onAccepted?: () => void,
    ) => {
      const state = getChatState()
      const {currentReferences, context} = state
      thread ||= getSelectedThread(state)

      // If the selected thread ID is set but the thread object wasn't found in the threads map
      // (e.g. threads were reloaded and the map was replaced), attempt to recover the thread
      // to avoid creating a new one and losing conversation context.
      if (!thread && state.selectedThreadID) {
        const recoveredThread = await manager.fetchLatestThread(state.selectedThreadID)
        if (recoveredThread && recoveredThread.id === state.selectedThreadID) {
          thread = recoveredThread
        }
      }

      reloadQuota('handle_message_submit')

      const trimmedContent = content.trim()
      if (trimmedContent === '') return false

      const referencesToUse = currentReferences
      // Mark content preview items that correspond to the references as not user-edited so that they don't automatically
      // get closed when the chat reducer clears the currentReferences.
      for (const ref of referencesToUse) {
        const item = makeVersionedItemFromReference({ref, messageIndex: nextMessageIndex})
        if (item != null) updateItem({...item, isUserEdited: false})
      }

      let accepted = false
      const chatMessageParams = {
        thread,
        content,
        references: [...referencesToUse, ...(additionalReferences || [])],
        topic: currentTopicRef.current,
        context,
        customInstructions: state.customInstructions,
        model: model || state.model,
        customCopilotId: initialCustomCopilotId ?? customCopilotId,
        mediaContent,
        onAccepted: () => {
          if (accepted) return
          accepted = true
          onAccepted?.()
        },
      } satisfies Parameters<typeof manager.sendChatMessage>[0]

      if (isSharedThread) {
        const duplicate = await manager.continueSharedThread(threadIdFromRoute)

        if (duplicate) {
          const {thread: newThread, messages: newMessages} = duplicate
          const parentMessageId = newMessages.at(-1)?.id
          await manager.sendChatMessage({...chatMessageParams, thread: newThread, parentMessageId})
        }
      } else {
        await manager.sendChatMessage(chatMessageParams)
      }

      return accepted
    },
    [
      getChatState,
      reloadQuota,
      customCopilotId,
      isSharedThread,
      manager,
      threadIdFromRoute,
      nextMessageIndex,
      updateItem,
      currentTopicRef,
    ],
  )
}
